import { useTranslation } from 'react-i18next'
import { common } from '@ocean/ui'
import { DateUtils } from '@ocean/utils'
import { NavigationUtils } from 'utils'
import { namespace } from 'modules/Notifications/localization'
import { NotificationItem, NotificationType } from 'modules/Notifications/types'
import { namespace as companyNoticeDetailsNamespace } from 'modules/activityDetails/CompanyNoticeDetails/localization'
import { namespace as surveyDetailsNamespace } from 'modules/activityDetails/SurveyDetails/localization'
import { ReactComponent as Time } from 'assets/icons/i-time.svg'
import {
  ItemLink,
  TitleWrapper,
  DescriptionWrapper,
  DeadlineWrapper,
  Deadline,
  DateCreatedWrapper,
} from './HighlightedNotificationsFactory.styled'

const { Typography } = common

const I18_PATH = `${namespace}:notifications.highlighted`
const I18_PATH_COMPANY_NOTICE_DETAILS = `${companyNoticeDetailsNamespace}:companyNoticeDetails`
const I18_PATH_SURVEY_DETAILS = `${surveyDetailsNamespace}:surveyDetails`

type HighlightedNotificationsItemProps = {
  id: Guid
  detailsUrl: string
  title: string
  description?: string
  dateCreated: string
  deadline: Nullable<string>
  onClick?: (id: Guid) => void
}

const HighlightedNotificationsItem: React.FC<HighlightedNotificationsItemProps> = props => {
  const { t } = useTranslation()
  const { id, detailsUrl, title, description, dateCreated, deadline, onClick } = props

  function handleLinkClick() {
    if (onClick) {
      onClick(id)
    }
  }

  return (
    <ItemLink to={detailsUrl} onClick={handleLinkClick} data-test='HighlightedNotificationsItem'>
      <TitleWrapper>{title}</TitleWrapper>
      {description && (
        <DescriptionWrapper>
          <Typography variant='small'>
            <span dangerouslySetInnerHTML={{ __html: description }} />
          </Typography>
        </DescriptionWrapper>
      )}
      {deadline && (
        <DeadlineWrapper>
          <Deadline>
            {t(`${I18_PATH}.deadline`, { date: DateUtils.formatShortDate(deadline) })}
          </Deadline>
        </DeadlineWrapper>
      )}
      <DateCreatedWrapper>
        <Time />
        <span>{DateUtils.formatDistanceToNow(new Date(dateCreated), true)}</span>
      </DateCreatedWrapper>
    </ItemLink>
  )
}

type HighlightedNotificationsFactoryProps = {
  item: NotificationItem
  companyNoticeAlias?: string
  onMarkAsRead: (id: Guid) => void
}

const HighlightedNotificationsFactory: React.FC<HighlightedNotificationsFactoryProps> = props => {
  const { t } = useTranslation()
  const { item, companyNoticeAlias, onMarkAsRead } = props

  function getResultDetailsUrl(baseUrl: string, resultId: Nullable<Guid>) {
    return resultId ? `${baseUrl}?resultId=${resultId}` : baseUrl
  }

  function renderNotification(notification: NotificationItem) {
    const { type, id, dateCreated, deadline, name, parentEntityId, resultId } = notification
    const routeUrl = NavigationUtils.getHighlightedNotificationDetailsRoute(type)

    switch (type) {
      case NotificationType.CompanyNotice: {
        const baseUrl = `${routeUrl}/${parentEntityId}/${id}`
        const detailsUrl = getResultDetailsUrl(baseUrl, resultId)
        const configurationTitle =
          companyNoticeAlias || t(`${I18_PATH_COMPANY_NOTICE_DETAILS}.title`)
        return (
          <HighlightedNotificationsItem
            id={id}
            detailsUrl={detailsUrl}
            title={configurationTitle as string}
            description={t(`${I18_PATH}.companyNoticeDescription`, {
              companyNoticeAlias: configurationTitle,
              title: `<b>${name}</b>`,
            })}
            dateCreated={dateCreated}
            deadline={deadline}
          />
        )
      }
      case NotificationType.Survey: {
        const baseUrl = `${routeUrl}/${parentEntityId}`
        const detailsUrl = getResultDetailsUrl(baseUrl, resultId)
        return (
          <HighlightedNotificationsItem
            id={id}
            detailsUrl={detailsUrl}
            title={t(`${I18_PATH_SURVEY_DETAILS}.title`)}
            description={t(`${I18_PATH}.surveyDescription`, {
              title: `<b>${name}</b>`,
              deadlineSpan: DateUtils.formatDistance(new Date(deadline as string), new Date()),
            })}
            dateCreated={dateCreated}
            deadline={deadline}
            onClick={onMarkAsRead}
          />
        )
      }
      default:
        return null
    }
  }

  return renderNotification(item)
}

export default HighlightedNotificationsFactory
